import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaUnlink,
  FaLock,
  FaShieldAlt,
  FaCalendarCheck,
  FaSync,
  FaExclamationTriangle,
  FaBuilding,
  FaUnlockAlt,
  FaQuestionCircle,
  FaTimes,
} from "react-icons/fa";
import "./FamilyMemberAuditLogTab.css";

/**
 * FamilyMemberAuditLogTab — AuditLog viewer for CORP-FAMILY-002 / 004 Activity tab.
 *
 * Surfaces the last N (default 50) corp-family:* + corp-family-member:*
 * events for the selected organization. Reverse chronological. America/Phoenix
 * MST timestamps per CLAUDE.md timezone rule.
 *
 * CORP-FAMILY-004 (Sprint 14) polish:
 *  - Section A: empty-state-with-CTA when no org is selected — does NOT query
 *    the BE; renders a `[Go to Enrollments]` button that triggers
 *    `onPickOrganization()` so the parent flips the tab strip.
 *  - Section C: reads the BE response shape Simone shipped in PR #209 —
 *    `event.staffId.firstName/lastName` (populated User) for ACTOR,
 *    `event.metadata.managerStaffName` + `event.metadata.enrollmentEmployeeName`
 *    for MANAGER + ENROLLMENT. Soft-fail contract: `*Name` keys are OMITTED
 *    (not null) on populate failure -> em-dash fallback.
 *  - `corp-family:cooldown-overridden` (PR #207 / CORP-FAMILY-003) added to
 *    EVENT_META so the new event type renders with its friendly label +
 *    teal-accent strip instead of falling through to the unknown-event branch.
 *  - `corp-family:unlink-rejected-bad-pin` MANAGER column renders em-dash by
 *    design — the audit hook writes BEFORE manager identity is established
 *    (no `metadata.managerStaffId` to populate).
 *  - Universal humanize() safety net: any unknown event-type slug is
 *    capitalized + underscores/colons/dashes replaced with spaces so the raw
 *    enum NEVER leaks to the UI.
 *
 * Backward-compat with the Sprint 13 mocked-event fixture shape: every reader
 * falls back to the legacy flat keys (`event.actorName`, `event.managerName`,
 * `event.employeeName`, `event.timestamp`, etc.) when the BE-populated path
 * is absent. CORP-FAMILY-002 callers see ZERO regression.
 *
 * Two anti-abuse event types are visually highlighted (per Zuri spec §
 * "Anti-abuse highlight treatment" and Roderick MSG-366-reply LOCK at ~5%
 * row tint):
 *   - corp-family:link-rejected-cooldown (gold strip + ~5% gold row tint)
 *   - corp-family:unlink-rejected-bad-pin (red strip + ~5% red row tint)
 *
 * Default date range = "Last 30 days" per Roderick LOCK; switcher offers
 * 30 / 90 / All-time for quarterly cadence.
 *
 * Props:
 *  - organizationId: string — currently-selected org (may be empty -> Section A)
 *  - fetchAuditLog: ({ organizationId, dateFrom, limit }) => Promise<{ data: { events: [...] }}>
 *  - onPickOrganization?: () => void — CORP-FAMILY-004 Section A; invoked when
 *    staff taps the `[Go to Enrollments]` button so the parent can switch the
 *    page-level tab strip back to Enrollments where the org-picker lives. If
 *    omitted, the button is hidden (defensive: no dead control).
 */
const DATE_RANGE_OPTIONS = [
  { value: "30d", label: "Last 30 days", days: 30 },
  { value: "90d", label: "Last 90 days", days: 90 },
  { value: "all", label: "All time", days: null },
];

const EVENT_META = {
  "corp-family:linked": {
    label: "Linked",
    icon: FaCheckCircle,
    accent: "success",
  },
  "corp-family:unlinked": {
    label: "Unlinked",
    icon: FaUnlink,
    accent: "muted",
  },
  "corp-family:link-rejected-cooldown": {
    label: "Link rejected — cooldown active",
    icon: FaLock,
    accent: "gold",
    antiAbuse: true,
  },
  "corp-family:unlink-rejected-bad-pin": {
    label: "Unlink rejected — bad PIN",
    icon: FaShieldAlt,
    accent: "error",
    antiAbuse: true,
  },
  "corp-family-member:booking-redeemed": {
    label: "Family-member booking redeemed",
    icon: FaCalendarCheck,
    accent: "teal",
  },
  // CORP-FAMILY-004 (Sprint 14) — surfaces PR #207 / CORP-FAMILY-003 cooldown-
  // override forensic event so Roderick can see who waived the 90-day guard
  // and why during quarterly review. Teal accent + unlock-icon per the
  // CORP-FAMILY-003 sibling spec (administrative gesture, not anti-abuse).
  "corp-family:cooldown-overridden": {
    label: "Cooldown override",
    icon: FaUnlockAlt,
    accent: "teal",
  },
};

// Manager-gated event types: the MANAGER column renders the populated name
// when present. For non-manager-gated types (linked, link-rejected-cooldown,
// booking-redeemed) the MANAGER column is em-dash by design (no manager).
const MANAGER_GATED_ACTIONS = new Set([
  "corp-family:unlinked",
  "corp-family:cooldown-overridden",
  // `corp-family:unlink-rejected-bad-pin` — IN enum but BE audit hook writes
  // BEFORE manager identity is established, so metadata.managerStaffId is
  // absent and the column will em-dash by design (no special-case needed).
]);

/**
 * humanize(action) — universal safety net for unknown event-type slugs.
 * Capitalize first letter, replace underscores/colons/dashes with spaces.
 * Guarantees no raw enum value ever leaks to the rendered UI even on event
 * types this FE bundle predates (forward-compat insurance per design spec
 * §"Universal humanize() safety net").
 */
const humanize = (action) => {
  if (!action || typeof action !== "string") return "—";
  const spaced = action.replace(/[_:-]+/g, " ").trim();
  if (!spaced) return "—";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Resolve the ACTOR name from a BE event. Prefers the populated
 * `event.staffId.firstName + lastName` shape (PR #209+); falls back to the
 * legacy flat `event.staffName` / `event.actorName` keys used by the
 * CORP-FAMILY-002 test fixtures. Returns null if no name is resolvable so
 * the caller can render the em-dash fallback.
 */
const resolveActorName = (event) => {
  if (!event) return null;
  if (event.staffId && typeof event.staffId === "object") {
    const first = event.staffId.firstName || "";
    const last = event.staffId.lastName || "";
    const joined = `${first} ${last}`.trim();
    if (joined) return joined;
  }
  if (event.staffName) return event.staffName;
  if (event.actorName) return event.actorName;
  return null;
};

/**
 * Resolve the MANAGER name from a BE event. Reads `metadata.managerStaffName`
 * (PR #209 populated path) with the same soft-fail contract: omitted key ->
 * null -> em-dash on render. Legacy fallback to top-level `event.managerName`
 * for CORP-FAMILY-002 fixture shape.
 */
const resolveManagerName = (event) => {
  if (!event) return null;
  const md = event.metadata || {};
  if (md.managerStaffName) return md.managerStaffName;
  if (event.managerName) return event.managerName;
  return null;
};

/**
 * Resolve the ENROLLMENT (employee) name from a BE event. Reads
 * `metadata.enrollmentEmployeeName` (PR #209 populated path); legacy
 * fallbacks for fixture shape: top-level `event.employeeName`,
 * `event.metadata.enrollmentEmployeeName` is the canonical key, and
 * `event.enrollmentEmployeeName` covers the historical flat shape.
 */
const resolveEnrollmentEmployeeName = (event) => {
  if (!event) return null;
  const md = event.metadata || {};
  if (md.enrollmentEmployeeName) return md.enrollmentEmployeeName;
  if (event.enrollmentEmployeeName) return event.enrollmentEmployeeName;
  if (event.employeeName) return event.employeeName;
  return null;
};

/**
 * Resolve the family-member name (rendered as the `-> Bob Smith` arrow tail
 * on the ENROLLMENT row, matching the deployed CORP-FAMILY-002 visual).
 * Pulled from `metadata.familyMemberName` first (BE writes this on
 * link/unlink/redeemed events) with fixture fallbacks.
 */
const resolveFamilyMemberName = (event) => {
  if (!event) return null;
  const md = event.metadata || {};
  if (md.familyMemberName) return md.familyMemberName;
  if (md.attemptedFamilyMemberName) return md.attemptedFamilyMemberName;
  if (event.familyMemberName) return event.familyMemberName;
  if (event.attemptedFamilyMemberName) return event.attemptedFamilyMemberName;
  return null;
};

/** Resolve the timestamp — BE sends `createdAt`; legacy fixtures send `timestamp`. */
const resolveTimestamp = (event) => {
  if (!event) return null;
  return event.createdAt || event.timestamp || null;
};

/** Resolve cooldownEnd (cooldown-rejection event) — metadata first, fixture flat fallback. */
const resolveCooldownEnd = (event) => {
  if (!event) return null;
  const md = event.metadata || {};
  return md.cooldownEnd || event.cooldownEnd || null;
};

/** Resolve booking-redeemed cents — metadata first, fixture flat fallback. */
const resolveAppliedRateCents = (event) => {
  if (!event) return null;
  const md = event.metadata || {};
  if (typeof md.appliedRateCents === "number") return md.appliedRateCents;
  if (typeof event.appliedRateCents === "number") return event.appliedRateCents;
  return null;
};

const resolveBookingDate = (event) => {
  if (!event) return null;
  const md = event.metadata || {};
  return md.bookingDate || event.bookingDate || null;
};

const formatTimestampMst = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const formatted = d.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "America/Phoenix",
    });
    // toLocaleString may use "12/03/2026, 14:22" — normalize to "2026-12-03 14:22 MST"
    const [datePart, timePart] = formatted.split(", ");
    if (datePart && timePart) {
      const [mm, dd, yyyy] = datePart.split("/");
      return `${yyyy}-${mm}-${dd} ${timePart} MST`;
    }
    return `${formatted} MST`;
  } catch (_err) {
    return `${iso} MST`;
  }
};

const formatDateMst = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "America/Phoenix",
    }).concat(" MST");
  } catch (_err) {
    return iso;
  }
};

const computeDateFrom = (rangeValue) => {
  const option = DATE_RANGE_OPTIONS.find((opt) => opt.value === rangeValue);
  if (!option || option.days === null) return null;
  const d = new Date();
  d.setDate(d.getDate() - option.days);
  return d.toISOString();
};

const truncate = (text, max = 80) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

const FamilyMemberAuditLogTab = ({
  organizationId,
  fetchAuditLog,
  onPickOrganization,
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("30d");

  // D2 polish (CORP-FAMILY-006): event-row tap-to-detail-drawer.
  // The drawer slides in from the right and shows the full event payload
  // (un-truncated reason text, full timestamp incl. seconds, raw metadata
  // KV-list). Closed by clicking the close button OR pressing Escape.
  // Drawer state is intentionally NOT persisted across org/tab navigation —
  // it auto-closes on `organizationId` change AND on unmount (AC scenario 4).
  const [activeEvent, setActiveEvent] = useState(null);
  const previousFocusRef = useRef(null);
  const drawerCloseRef = useRef(null);

  // CORP-FAMILY-004 Section A: when no org is selected we render the
  // "Pick an organization" empty state and DO NOT query the BE. The early
  // return inside `load` enforces the contract; this `hasOrg` flag also
  // gates the filter bar visibility (per design spec §"Filter bar
  // visibility — HIDDEN in this state").
  const hasOrg = Boolean(organizationId);

  const load = useCallback(async () => {
    if (!organizationId || !fetchAuditLog) {
      setEvents([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetchAuditLog({
        organizationId,
        dateFrom: computeDateFrom(dateRange),
        limit: 50,
      });
      setEvents(response?.data?.events || []);
    } catch (_err) {
      setError("There was a problem loading the AuditLog. The data is safe; this is a fetch problem.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchAuditLog, dateRange]);

  useEffect(() => {
    load();
  }, [load]);

  // D2: close the drawer whenever organizationId changes (Scenario 4 — no
  // lingering drawer state across page navigation).
  useEffect(() => {
    setActiveEvent(null);
  }, [organizationId]);

  // D2: Escape closes the drawer; focus returns to the previously focused
  // element on close (a11y accommodation — matches deployed ManagerPinModal
  // dismissal posture). Listener registered only while a drawer is open.
  useEffect(() => {
    if (!activeEvent) return undefined;
    const handler = (event) => {
      if (event.key === "Escape") {
        setActiveEvent(null);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activeEvent]);

  // D2: focus the drawer's close button when it opens (a11y — keyboard users
  // land on a dismissable affordance). Restore focus to the row on close.
  useEffect(() => {
    if (activeEvent && drawerCloseRef.current) {
      drawerCloseRef.current.focus();
    } else if (!activeEvent && previousFocusRef.current) {
      try {
        previousFocusRef.current.focus();
      } catch (_e) {
        // Defensive: focused element may have unmounted; ignore.
      }
      previousFocusRef.current = null;
    }
  }, [activeEvent]);

  const openEventDrawer = useCallback((event) => {
    previousFocusRef.current = document.activeElement;
    setActiveEvent(event);
  }, []);

  const closeEventDrawer = useCallback(() => {
    setActiveEvent(null);
  }, []);

  const eventCount = useMemo(() => events.length, [events]);

  const renderEventRow = (event, index) => {
    // Unknown event types degrade gracefully via humanize() so no raw enum
    // ever leaks to the rendered UI (Section C universal safety net).
    const knownMeta = EVENT_META[event.action];
    const meta = knownMeta || {
      label: humanize(event.action),
      icon: FaQuestionCircle,
      accent: "muted",
    };
    const Icon = meta.icon;
    const rowClass = `family-audit-row accent-${meta.accent}${
      meta.antiAbuse ? " is-anti-abuse" : ""
    }`;

    const actorName = resolveActorName(event) || "—";
    const managerName = resolveManagerName(event);
    const employeeName = resolveEnrollmentEmployeeName(event);
    const familyMemberName = resolveFamilyMemberName(event);
    const timestampIso = resolveTimestamp(event);

    // ENROLLMENT row composition: "Alice Smith → Bob Smith" when both are
    // resolvable; em-dash when neither is. Matches the deployed
    // CORP-FAMILY-002 layout.
    const enrollmentDisplay = (() => {
      if (employeeName && familyMemberName) {
        return `${employeeName} → ${familyMemberName}`;
      }
      if (employeeName) return employeeName;
      if (familyMemberName) return `— → ${familyMemberName}`;
      return "—";
    })();

    // MANAGER row: only render the row at all for manager-gated event types
    // OR when the BE supplied a managerStaffName (defensive — future event
    // types added to the manager-gated set without updating this set still
    // render). Unlink-rejected-bad-pin is in EVENT_META but NOT in the
    // manager-gated set: by design `metadata.managerStaffId` is absent at
    // the audit-hook write point, so the row falls through to em-dash and
    // we suppress it to keep the layout clean.
    const showManagerRow =
      MANAGER_GATED_ACTIONS.has(event.action) || Boolean(managerName);

    // Column 3 secondary signal: reason text (truncated) takes priority for
    // events that carry it; for the anti-abuse + redeemed types we render
    // the type-specific signal per CORP-FAMILY-002 spec; for everything else
    // we fall through to the friendly label OR (unknown event) humanize().
    // Under NO circumstance does the raw action enum render here.
    const reasonText = event.reason || event.metadata?.reason || null;
    let column3Content = null;
    if (event.action === "corp-family:link-rejected-cooldown") {
      column3Content = (
        <span className="family-audit-col3-cooldown">
          Cooldown ends: {formatDateMst(resolveCooldownEnd(event))}
        </span>
      );
    } else if (event.action === "corp-family:unlink-rejected-bad-pin") {
      column3Content = <span className="family-audit-col3-security">Security event</span>;
    } else if (event.action === "corp-family-member:booking-redeemed") {
      // D3 polish (CORP-FAMILY-006): show date + time (was date only) in MST.
      // Zuri spec line 358 calls for booking date+time MST + applied rate;
      // Nia D3 confirmed the missing time component (web-qa MSG-356 line 2770).
      const cents = resolveAppliedRateCents(event);
      const bookingDate = resolveBookingDate(event);
      column3Content = (
        <span className="family-audit-col3-redeemed">
          {bookingDate ? formatTimestampMst(bookingDate) : null}
          {typeof cents === "number" ? ` · $${(cents / 100).toFixed(2)}` : null}
        </span>
      );
    } else if (reasonText) {
      column3Content = (
        <span className="family-audit-col3-reason" title={reasonText}>
          {truncate(reasonText, 80)}
        </span>
      );
    } else if (!knownMeta) {
      // Unknown event type: humanize the action so no raw enum leaks even
      // here. Forward-compat insurance per Section C universal safety net.
      column3Content = (
        <span className="family-audit-col3-reason">{humanize(event.action)}</span>
      );
    }

    return (
      <div
        key={event._id || `${event.action}-${timestampIso}-${index}`}
        className={`${rowClass} is-tappable`}
        role="button"
        tabIndex={0}
        aria-label={
          meta.antiAbuse
            ? `Anti-abuse signal: ${meta.label} — open details`
            : `${meta.label} — open details`
        }
        onClick={() => openEventDrawer(event)}
        onKeyDown={(e) => {
          // D2: keyboard activation (Enter / Space) opens the drawer for
          // a11y parity with mouse click.
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openEventDrawer(event);
          }
        }}
      >
        <div className="family-audit-row-strip" aria-hidden="true" />
        <div className="family-audit-row-body">
          <div className="family-audit-col1">
            <span className="family-audit-row-icon" aria-hidden="true">
              <Icon />
            </span>
            <div className="family-audit-col1-text">
              <span className="family-audit-row-label">{meta.label}</span>
              <span className="family-audit-row-timestamp">
                {formatTimestampMst(timestampIso)}
              </span>
            </div>
          </div>
          <div className="family-audit-col2">
            <div className="family-audit-row-line">
              <span className="family-audit-row-key">Actor:</span>
              <span className="family-audit-row-value">{actorName}</span>
            </div>
            {showManagerRow && (
              <div className="family-audit-row-line">
                <span className="family-audit-row-key">Manager:</span>
                <span className="family-audit-row-value">{managerName || "—"}</span>
              </div>
            )}
            <div className="family-audit-row-line">
              <span className="family-audit-row-key">Enrollment:</span>
              <span className="family-audit-row-value">{enrollmentDisplay}</span>
            </div>
          </div>
          <div className="family-audit-col3">{column3Content}</div>
        </div>
      </div>
    );
  };

  // CORP-FAMILY-004 Section A: no org selected -> empty-state-with-CTA. The
  // affordance is intentionally rendered INSIDE the Activity tab body
  // (per design spec §"Section A IA"); the canonical org-picker lives on
  // the Enrollments tab. We do NOT fetch and we do NOT render the filter
  // bar (showing filters without an org would imply "you can filter" when
  // there's nothing to filter).
  const renderNoOrgEmptyState = () => (
    <div className="family-audit-empty family-audit-empty-no-org" role="status">
      <div className="family-audit-empty-illustration" aria-hidden="true">
        <FaBuilding />
      </div>
      <h3>Pick an organization to see its activity</h3>
      <p>
        The Activity tab shows family-member link, unlink, cooldown, and bad-PIN
        events for the selected organization. Use the Enrollments tab to pick
        an organization.
      </p>
      {onPickOrganization ? (
        <button
          type="button"
          className="family-audit-empty-cta"
          onClick={onPickOrganization}
          aria-label="Go to Enrollments tab to pick an organization"
        >
          Go to Enrollments
        </button>
      ) : null}
    </div>
  );

  const renderBody = () => {
    if (!hasOrg) {
      return renderNoOrgEmptyState();
    }
    if (loading) {
      return (
        <div className="family-audit-skeleton-list" aria-busy="true" aria-live="polite">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className="family-audit-skeleton-row" />
          ))}
        </div>
      );
    }
    if (error) {
      return (
        <div className="family-audit-error" role="alert">
          <span className="family-audit-error-icon" aria-hidden="true">
            <FaExclamationTriangle />
          </span>
          <div className="family-audit-error-body">
            <strong>Couldn&apos;t load activity</strong>
            <p>{error}</p>
            <button
              type="button"
              className="family-audit-retry"
              onClick={load}
              aria-label="Retry loading family member activity"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    if (eventCount === 0) {
      return (
        <div className="family-audit-empty" role="status">
          <h3>No family-member activity yet</h3>
          <p>
            Once staff link or unlink a family member, those events will appear here.
            Reviewed quarterly for benefit-rotation patterns.
          </p>
        </div>
      );
    }
    return (
      <div className="family-audit-list" role="list">
        {events.map((event, idx) => renderEventRow(event, idx))}
      </div>
    );
  };

  // D2 polish (CORP-FAMILY-006): event detail drawer. Slides in from the
  // right; displays the full (un-truncated) event payload. The underlying
  // table remains scrollable (no body scroll-lock — Scenario 3 last bullet).
  // Renders metadata as a KV list; raw enum-action is humanized.
  const renderDetailDrawer = () => {
    if (!activeEvent) return null;
    const knownMeta = EVENT_META[activeEvent.action];
    const meta = knownMeta || {
      label: humanize(activeEvent.action),
      icon: FaQuestionCircle,
      accent: "muted",
    };
    const Icon = meta.icon;
    const actorName = resolveActorName(activeEvent) || "—";
    const managerName = resolveManagerName(activeEvent);
    const employeeName = resolveEnrollmentEmployeeName(activeEvent);
    const familyMemberName = resolveFamilyMemberName(activeEvent);
    const timestampIso = resolveTimestamp(activeEvent);
    const reasonText = activeEvent.reason || activeEvent.metadata?.reason || null;
    const metadata = activeEvent.metadata || {};
    const metadataEntries = Object.entries(metadata).filter(
      // Hide the keys we already render explicitly above to avoid duplicate
      // visual noise; leak the rest as a KV list for forensic depth.
      ([key]) =>
        ![
          "managerStaffName",
          "enrollmentEmployeeName",
          "familyMemberName",
        ].includes(key)
    );
    return (
      <>
        <div
          className="family-audit-drawer-backdrop"
          onMouseDown={closeEventDrawer}
          aria-hidden="true"
        />
        <aside
          className={`family-audit-drawer accent-${meta.accent}`}
          role="dialog"
          aria-modal="false"
          aria-labelledby="family-audit-drawer-title"
        >
          <header className="family-audit-drawer-header">
            <span className="family-audit-drawer-icon" aria-hidden="true">
              <Icon />
            </span>
            <h3 id="family-audit-drawer-title" className="family-audit-drawer-title">
              {meta.label}
            </h3>
            <button
              ref={drawerCloseRef}
              type="button"
              className="family-audit-drawer-close"
              onClick={closeEventDrawer}
              aria-label="Close event detail"
            >
              <FaTimes aria-hidden="true" />
            </button>
          </header>
          <div className="family-audit-drawer-body">
            <dl className="family-audit-drawer-kv">
              <div className="family-audit-drawer-kv-row">
                <dt>Timestamp</dt>
                <dd>{formatTimestampMst(timestampIso)}</dd>
              </div>
              <div className="family-audit-drawer-kv-row">
                <dt>Actor</dt>
                <dd>{actorName}</dd>
              </div>
              <div className="family-audit-drawer-kv-row">
                <dt>Manager</dt>
                <dd>{managerName || "—"}</dd>
              </div>
              <div className="family-audit-drawer-kv-row">
                <dt>Enrollment</dt>
                <dd>
                  {employeeName || "—"}
                  {familyMemberName ? ` → ${familyMemberName}` : ""}
                </dd>
              </div>
              {reasonText ? (
                <div className="family-audit-drawer-kv-row family-audit-drawer-reason">
                  <dt>Reason</dt>
                  <dd>{reasonText}</dd>
                </div>
              ) : null}
            </dl>
            {metadataEntries.length > 0 ? (
              <details className="family-audit-drawer-metadata">
                <summary>Raw metadata</summary>
                <dl className="family-audit-drawer-kv family-audit-drawer-kv-metadata">
                  {metadataEntries.map(([key, value]) => (
                    <div key={key} className="family-audit-drawer-kv-row">
                      <dt>{key}</dt>
                      <dd>
                        {typeof value === "object" && value !== null
                          ? JSON.stringify(value)
                          : String(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </details>
            ) : null}
          </div>
        </aside>
      </>
    );
  };

  return (
    <div className="family-audit-tab">
      <div className="family-audit-tab-header">
        <h2>Activity — Family Member Events</h2>
        <p>
          Recent link / unlink / cooldown-rejection / bad-PIN-attempt events for
          this organization. Reviewed quarterly for benefit-rotation patterns.
          All times in MST.
        </p>
      </div>

      {hasOrg ? (
        <div className="family-audit-filter-bar">
          <div className="family-audit-filter-group" role="group" aria-label="Date range filter">
            {DATE_RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`family-audit-filter-chip${
                  dateRange === option.value ? " is-active" : ""
                }`}
                onClick={() => setDateRange(option.value)}
                aria-pressed={dateRange === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="family-audit-refresh"
            onClick={load}
            disabled={loading}
            aria-label="Refresh activity"
          >
            <FaSync aria-hidden="true" />
            <span className="family-audit-refresh-label">Refresh</span>
          </button>
        </div>
      ) : null}

      {hasOrg && !loading && !error && eventCount > 0 ? (
        <p className="family-audit-count">
          {eventCount} event{eventCount === 1 ? "" : "s"}
        </p>
      ) : null}

      {renderBody()}

      {renderDetailDrawer()}
    </div>
  );
};

export default FamilyMemberAuditLogTab;
